import { Customer, FlightType, OrgMember, Payload, Pilot, Project } from '../types/api';
import { useFlightMetadataOptionsQuery } from './flightMetadataOptions.queries';
import { useAddCustomer, useAddPayload, useAddProject } from './flightMetadataOptions.mutations';

interface FlightMetadataOptions {
  flightTypes: FlightType[];
  payloads: Payload[];
  pilots: Pilot[];
  customers: Customer[];
  projects: Project[];
  airspaceObservers: OrgMember[];
  payloadOperators: OrgMember[];
  loading: boolean;
  error: string | null;
  addCustomer: (name: string) => Promise<Customer>;
  addProject: (name: string) => Promise<Project>;
  addPayload: (name: string, weight?: number) => Promise<Payload>;
}

// STD-015 thin wrapper — public contract unchanged. Server state lives in
// flightMetadataOptions.queries.ts and .mutations.ts. Errors are surfaced as
// the inline `error` string (not toasted) to match previous behaviour.
export const useFlightMetadataOptions = ({
  enabled = true,
}: { enabled?: boolean } = {}): FlightMetadataOptions => {
  const query = useFlightMetadataOptionsQuery({ enabled });
  const addCustomerMutation = useAddCustomer();
  const addProjectMutation = useAddProject();
  const addPayloadMutation = useAddPayload();

  const addCustomer = (name: string): Promise<Customer> => addCustomerMutation.mutateAsync(name);

  const addProject = (name: string): Promise<Project> => addProjectMutation.mutateAsync(name);

  const addPayload = (name: string, weight?: number): Promise<Payload> =>
    addPayloadMutation.mutateAsync({ name, weight });

  return {
    flightTypes: query.data?.flightTypes ?? [],
    payloads: query.data?.payloads ?? [],
    pilots: query.data?.pilots ?? [],
    customers: query.data?.customers ?? [],
    projects: query.data?.projects ?? [],
    airspaceObservers: query.data?.airspaceObservers ?? [],
    payloadOperators: query.data?.payloadOperators ?? [],
    loading: query.isLoading,
    error: query.isError ? 'Failed to load options' : null,
    addCustomer,
    addProject,
    addPayload,
  };
};
